import Icon from "@components/clinic/Icon";
import { formatDateTime, roleLabel, rolePill } from "@utils/userFormatters";

const PRESET_LABELS = {
  CLINICAL: { title: "Clinical Notes", emoji: "📝", pill: "bg-sky-50 text-sky-700 ring-sky-200" },
  FRONTDESK: { title: "FrontDesk Bookings", emoji: "📅", pill: "bg-amber-50 text-amber-800 ring-amber-200" },
  BOTH: { title: "Both", emoji: "✨", pill: "bg-emerald-50 text-emerald-800 ring-emerald-200" },
};

function AccessPill({ preset }) {
  const key = String(preset || "").toUpperCase();
  const meta = PRESET_LABELS[key];
  if (!meta) return <span className="text-xs text-slate-400">Not set</span>;

  return (
    <span
      className={`inline-flex items-center gap-2 px-2.5 py-1.5 rounded-full text-xs font-medium ring-1 ${meta.pill}`}
      title={meta.title}
    >
      <span aria-hidden>{meta.emoji}</span>
      <span className="whitespace-nowrap">{meta.title}</span>
    </span>
  );
}

function UserCard({
  u,
  onEditAccess,
  onRemoveClick,
  removingId,
  updatingUserId,
}) {
  const isOwnerRow = !!u.is_owner;
  const isBusyRemove = removingId === u.id;
  const isBusyUpdate = updatingUserId === u.id;

  return (
    <div className="rounded-2xl border bg-white p-4">
      {/* Top row */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="relative shrink-0">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-500/20 via-purple-500/20 to-emerald-500/20 border flex items-center justify-center text-slate-700 font-semibold">
              {(u.email || "?").slice(0, 1).toUpperCase()}
            </div>
            {isOwnerRow && (
              <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-white border flex items-center justify-center">
                <Icon name="shield" className="w-3.5 h-3.5 text-purple-700" />
              </div>
            )}
          </div>

          <div className="min-w-0">
            <div className="font-semibold text-slate-900 truncate">
              {u.email}
            </div>
            <div className="text-xs text-slate-500 truncate">ID: {u.id}</div>
          </div>
        </div>

        {/* Owner badge */}
        {isOwnerRow ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-purple-800 bg-purple-50 ring-1 ring-purple-200 px-2 py-1 rounded-full shrink-0">
            <Icon name="shield" className="w-4 h-4" />
            Owner
          </span>
        ) : null}
      </div>

      {/* Role + last login */}
      <div className="mt-3 flex items-center justify-between gap-3">
        <span
          className={`inline-flex items-center px-2 py-1 text-xs rounded-full ring-1 ${rolePill(
            u.role
          )}`}
          title={roleLabel(u.role)}
        >
          {roleLabel(u.role)}
        </span>

        <div className="text-xs text-slate-600 text-right">
          <div className="text-[11px] text-slate-500">Last login</div>
          <div className="truncate">{formatDateTime(u.last_login_at)}</div>
        </div>
      </div>

      {/* Access */}
      <div className="mt-3">
        <div className="text-[11px] font-medium text-slate-500 mb-1">Access</div>
        {isOwnerRow ? (
          <span className="text-xs text-slate-400">Protected</span>
        ) : (
          <AccessPill preset={u.permission_preset} />
        )}
      </div>

      {/* Actions */}
      <div className="mt-4 flex gap-2">
        {isOwnerRow ? (
          <div className="text-xs text-slate-400">Protected account</div>
        ) : (
          <>
            <button
              type="button"
              className="flex-1 text-sm px-3 py-2 rounded-xl border hover:bg-slate-50 disabled:opacity-50"
              onClick={() => onEditAccess?.(u)}
              disabled={isBusyUpdate}
              title="Edit role & access"
            >
              {isBusyUpdate ? "Saving…" : "Edit access"}
            </button>

            <button
              className="text-sm px-3 py-2 rounded-xl border
                         hover:bg-red-50 hover:border-red-200 hover:text-red-700
                         disabled:opacity-50 transition-colors"
              onClick={() => onRemoveClick?.(u)}
              disabled={isBusyRemove || isBusyUpdate}
              title="Remove user"
            >
              {isBusyRemove ? "Removing…" : "Remove"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function UsersTable({
  users,
  total,
  loading,
  error,
  onRemoveClick,
  removingId,
  updatingUserId,
  onEditAccess,
}) {
  const safeUsers = users || [];
  const empty = !loading && !error && safeUsers.length === 0;

  return (
    <div className="flex-1 min-h-0 overflow-hidden rounded-2xl border bg-white">
      <div className="px-5 py-3 border-b flex items-center justify-between">
        <div className="font-semibold text-slate-900 flex items-center gap-2">
          <span className="p-2 rounded-xl bg-slate-50 border">
            <Icon name="users" className="w-4 h-4" />
          </span>
          Clinic users
        </div>
        <div className="text-sm text-slate-500">
          Showing{" "}
          <span className="font-semibold text-slate-900">
            {safeUsers.length}
          </span>{" "}
          / <span className="font-semibold text-slate-900">{total}</span>
        </div>
      </div>

      {loading && <div className="p-5 text-slate-600">Loading users…</div>}
      {error && <div className="p-5 text-red-600">{error}</div>}

      {empty && (
        <div className="p-10 text-center">
          <div className="mx-auto w-fit p-4 rounded-2xl bg-gradient-to-br from-sky-500/15 via-purple-500/15 to-emerald-500/15 border">
            <Icon name="spark" className="w-7 h-7" />
          </div>
          <div className="mt-3 font-semibold text-slate-900">
            No users found
          </div>
          <div className="text-sm text-slate-600 mt-1">
            Add a user above to get started.
          </div>
        </div>
      )}

      {!loading && !error && safeUsers.length > 0 && (
        <>
          {/* Mobile cards */}
          <div className="md:hidden p-3 space-y-3">
            {safeUsers.map((u) => (
              <UserCard
                key={u.id}
                u={u}
                onEditAccess={onEditAccess}
                onRemoveClick={onRemoveClick}
                removingId={removingId}
                updatingUserId={updatingUserId}
              />
            ))}
          </div>

          {/* Tablet/Desktop table */}
          <div className="hidden md:block overflow-x-auto h-full">
            <table className="w-full text-sm min-w-[980px]">
              <thead className="text-left text-slate-500 sticky top-0 bg-white">
                <tr className="border-b">
                  <th className="p-3">User</th>
                  <th className="p-3">Role</th>
                  <th className="p-3">Access</th>
                  <th className="p-3">Owner</th>
                  <th className="p-3">Last login</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>

              <tbody>
                {safeUsers.map((u) => {
                  const isOwnerRow = !!u.is_owner;
                  const isBusyRemove = removingId === u.id;
                  const isBusyUpdate = updatingUserId === u.id;

                  return (
                    <tr
                      key={u.id}
                      className="border-b hover:bg-slate-50/70 transition-colors"
                    >
                      {/* User */}
                      <td className="p-3">
                        <div className="flex items-center gap-3">
                          <div className="relative">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-500/20 via-purple-500/20 to-emerald-500/20 border flex items-center justify-center text-slate-700 font-semibold">
                              {(u.email || "?").slice(0, 1).toUpperCase()}
                            </div>
                            {isOwnerRow && (
                              <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-white border flex items-center justify-center">
                                <Icon
                                  name="shield"
                                  className="w-3.5 h-3.5 text-purple-700"
                                />
                              </div>
                            )}
                          </div>

                          <div>
                            <div className="font-semibold text-slate-900">
                              {u.email}
                            </div>
                            <div className="text-xs text-slate-500">
                              ID: {u.id}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Role */}
                      <td className="p-3">
                        <span
                          className={`inline-flex items-center px-2 py-1 text-xs rounded-full ring-1 ${rolePill(
                            u.role
                          )}`}
                          title={roleLabel(u.role)}
                        >
                          {roleLabel(u.role)}
                        </span>
                      </td>

                      {/* Access */}
                      <td className="p-3">
                        {isOwnerRow ? (
                          <span className="text-xs text-slate-400">Protected</span>
                        ) : (
                          <AccessPill preset={u.permission_preset} />
                        )}
                      </td>

                      {/* Owner */}
                      <td className="p-3">
                        {isOwnerRow ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-purple-800 bg-purple-50 ring-1 ring-purple-200 px-2 py-1 rounded-full">
                            <Icon name="shield" className="w-4 h-4" />
                            Owner
                          </span>
                        ) : (
                          <span className="text-slate-600">No</span>
                        )}
                      </td>

                      {/* Last login */}
                      <td className="p-3 text-slate-700">
                        {formatDateTime(u.last_login_at)}
                      </td>

                      {/* Actions */}
                      <td className="p-3 text-right">
                        {isOwnerRow ? (
                          <span className="text-xs text-slate-400">
                            Protected
                          </span>
                        ) : (
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border
                                         hover:bg-slate-50 disabled:opacity-50 whitespace-nowrap"
                              onClick={() => onEditAccess?.(u)}
                              disabled={isBusyUpdate}
                              title="Edit role"
                            >
                              {isBusyUpdate ? "Saving…" : "Edit role"}
                            </button>
                            <button
                              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border
                                         hover:bg-red-50 hover:border-red-200 hover:text-red-700
                                         disabled:opacity-50 transition-colors whitespace-nowrap"
                              onClick={() => onRemoveClick?.(u)}
                              disabled={isBusyRemove || isBusyUpdate}
                              title="Remove user"
                            >
                              <Icon name="trash" className="w-4 h-4" />
                              {isBusyRemove ? "Removing…" : "Remove"}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div className="p-4 text-xs text-slate-500 border-t">
              Tip: Use <span className="font-semibold">Edit role</span> to
              change a user's role.
            </div>
          </div>
        </>
      )}
    </div>
  );
}
